import { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleGenAI } from '@google/genai';
import type { ChatMessage } from '@/src/types';
import { CHAT_CONFIG } from '@/src/constants';

interface UseChatAIOptions {
  systemPrompt: string;
  maxMessages?: number;
  initialMessage?: string;
}

interface UseChatAIReturn {
  messages: ChatMessage[];
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  messageCount: number;
  isLimitReached: boolean;
  sendMessage: () => Promise<void>;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  scrollRef: React.RefObject<HTMLDivElement>;
}

/**
 * Custom hook to manage AI chat functionality
 */
export function useChatAI({
  systemPrompt,
  maxMessages = CHAT_CONFIG.maxMessages,
  initialMessage = 'Hola, ¿en qué puedo ayudarte?',
}: UseChatAIOptions): UseChatAIReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'ai', text: initialMessage },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isLimitReached = messageCount >= maxMessages;

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading || isLimitReached) return;

    // Check for API Key
    if (!process.env.API_KEY) {
      setMessages((prev) => [
        ...prev,
        { role: 'ai', text: 'Error: API Key no configurada.' },
      ]);
      return;
    }

    const userText = input;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: userText }]);
    setIsLoading(true);
    setMessageCount((prev) => prev + 1);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

      // Build conversation history
      const history = messages.map((m) => `${m.role}: ${m.text}`).join('\n');

      const response = await ai.models.generateContent({
        model: CHAT_CONFIG.modelName,
        contents: [
          {
            role: 'user',
            parts: [
              {
                text: `${systemPrompt}\n\nHistorial:\n${history}\n\nUsuario: ${userText}`,
              },
            ],
          },
        ],
      });

      const aiText =
        response.text || 'Lo siento, no pude procesar tu mensaje.';
      setMessages((prev) => [...prev, { role: 'ai', text: aiText }]);
    } catch (error) {
      console.error('Chat AI Error:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'ai', text: 'Error de conexión. Intenta de nuevo.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, isLimitReached, messages, systemPrompt]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  return {
    messages,
    input,
    setInput,
    isLoading,
    messageCount,
    isLimitReached,
    sendMessage,
    handleKeyDown,
    scrollRef: scrollRef as React.RefObject<HTMLDivElement>,
  };
}

export default useChatAI;
